import { Component, type ErrorInfo, type ReactNode } from 'react';
import { serializeError } from '@/lib/observability';

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
  message: string | null;
}

/**
 * App-level error boundary. Catches render errors so a single faulty
 * component can't white-screen the whole app.
 *
 * "Hard reset" unregisters service workers + clears HOTTR local data —
 * the usual fix when a half-finished sign-in or stale offline cache leaves
 * someone stuck on this screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: null };

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      message: error?.message || 'Unknown error',
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[error-boundary]', serializeError(error), info.componentStack);
  }

  private softReload = () => {
    window.location.assign('/');
  };

  private hardReset = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      // Clear HOTTR keys only — leave other site data alone
      const drop: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && (k.startsWith('hottr') || k.startsWith('sb-'))) drop.push(k);
      }
      drop.forEach((k) => localStorage.removeItem(k));
      sessionStorage.clear();
    } catch {
      /* best-effort */
    }
    window.location.assign('/?reset=1');
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1.25rem',
          background: '#050608',
          color: '#FFFFFF',
          fontFamily: 'Poppins, system-ui, sans-serif',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            fontSize: '1.5rem',
          }}
        >
          HOTTR<span style={{ color: '#FF0CB6' }}>.</span>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.72)', maxWidth: '36ch', margin: 0 }}>
          Something glitched. Try a soft reload, or a hard reset if it keeps happening.
        </p>
        {this.state.message ? (
          <p
            style={{
              color: 'rgba(255,255,255,0.4)',
              maxWidth: '42ch',
              margin: 0,
              fontSize: '0.75rem',
              wordBreak: 'break-word',
            }}
          >
            {this.state.message}
          </p>
        ) : null}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={this.softReload}
            style={{
              border: '1px solid #FF0CB6',
              background: '#FF0CB6',
              color: '#FFFFFF',
              padding: '0.6rem 1.3rem',
              borderRadius: '2px',
              cursor: 'pointer',
              fontFamily: 'Poppins, system-ui, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.78rem',
            }}
          >
            Reload
          </button>
          <button
            type="button"
            onClick={() => void this.hardReset()}
            style={{
              border: '1px solid rgba(255,255,255,0.25)',
              background: 'transparent',
              color: '#FFFFFF',
              padding: '0.6rem 1.3rem',
              borderRadius: '2px',
              cursor: 'pointer',
              fontFamily: 'Poppins, system-ui, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.78rem',
            }}
          >
            Hard reset
          </button>
        </div>
      </div>
    );
  }
}
